import type { QueryClient } from '@tanstack/react-query';

import { cohortQueryKeys } from './queryKeys';

export interface InvalidateCohortParams {
  cohortId: string;
  projectId: string;
  versionId?: string;
}

/**
 * Single choke point for invalidating all ``cohortQueryKeys``-namespaced
 * caches for a specific cohort. Every mutation, socket handler, and resolver
 * that changes cohort state should call this instead of bluntly invalidating
 * ``cohortQueryKeys.all``.
 *
 * Note: ``projectPageKeys`` (artifact manifest + activity feed) cannot be
 * included here without creating a build cycle. Project-page cache
 * invalidation on the mutation path is handled by ``useProjectPageCohortSync``
 * in ``feature-project-page``, which subscribes to the mutation cache.
 */
export const invalidateCohort = (
  queryClient: QueryClient,
  { cohortId, projectId, versionId }: InvalidateCohortParams
): Promise<void[]> => {
  const promises: Promise<void>[] = [
    queryClient.invalidateQueries({ queryKey: cohortQueryKeys.profile(cohortId) }),
    queryClient.invalidateQueries({ queryKey: cohortQueryKeys.list(projectId) }),
  ];
  if (versionId) {
    promises.push(queryClient.invalidateQueries({ queryKey: cohortQueryKeys.version(versionId) }));
    promises.push(
      queryClient.invalidateQueries({
        queryKey: cohortQueryKeys.assetStaleness('cohort', versionId),
      })
    );
    // No usage lookup for which ADs depend on this cohort — invalidate all AD staleness reads.
    promises.push(
      queryClient.invalidateQueries({
        queryKey: cohortQueryKeys.assetStalenessKind('analytical_dataset'),
      })
    );
  }
  return Promise.all(promises);
};
